import { FC, RefObject, useCallback, useEffect } from 'react'

import { ActionDropdown } from 'components/ActionDropdown/ActionDropdown'
import { useChannel } from 'hooks/useChannel'
import { useQueueIdsFilteredBySpecialisation } from 'hooks/useQueueIdsFilteredBySpecialisation'
import { useTicketResponseData } from 'hooks/useTicketResponseData'
import { useTicketsSorting } from 'hooks/useTicketsSorting'
import {
  useTicketIdsMutation,
  useTicketsInfiniteQuery,
} from 'modules/api/analytics/queries'
import { ResourcesPanelCopyResourcesButton } from 'pages/Monitoring/components/ResourcesPanel/ResourcesPanelCopyResourcesButton'
import { ResourcesPanelList } from 'pages/Monitoring/components/ResourcesPanel/ResourcesPanelList'
import { ResourcesPanelSection } from 'pages/Monitoring/components/ResourcesPanel/ResourcesPanelSection'
import { useAdditionalFilters } from 'pages/Monitoring/GlobalFilters/AdditionalFilters/AdditionalFiltersContext'
import { ResourcesSideParams, TicketsRequestParams } from 'types'
import { pluralize } from 'utils/pluralize'

type QueueHealthTicketsProps = {
  resourcesSideParams: ResourcesSideParams
  scrollRef: RefObject<HTMLDivElement | null>
}
export const QueueHealthTickets: FC<QueueHealthTicketsProps> = ({
  resourcesSideParams,
  scrollRef,
}) => {
  const { entityName, channel } = useChannel()
  const { preparedFiltersForRequest } = useAdditionalFilters()
  const { activeSorting, sortingOptions, sortingValue, onSortingSelect, onResetSorting } =
    useTicketsSorting()

  const queueIds = useQueueIdsFilteredBySpecialisation(
    resourcesSideParams?.specialisation?.id,
  )
  const ticketsParams: TicketsRequestParams = {
    ...resourcesSideParams!,
    filter: preparedFiltersForRequest,
    channel,
    queueIds,
    sortBy: activeSorting.sortBy,
    page: {
      sortOrder: activeSorting.sortOrder,
    },
  }

  const {
    data: ticketsResponse,
    fetchNextPage,
    isPending,
    hasNextPage,
  } = useTicketsInfiniteQuery(
    ticketsParams,
    Boolean(resourcesSideParams) && queueIds !== undefined,
  )
  const { mutateAsync: getTicketIds } = useTicketIdsMutation()
  const { tickets, ticketCount } = useTicketResponseData(ticketsResponse)

  useEffect(onResetSorting, [resourcesSideParams.filterType, onResetSorting])

  const keyExtractor = useCallback((index: number) => tickets[index].id, [tickets])

  return (
    <ResourcesPanelSection
      title={pluralize(entityName, ticketCount)}
      action={
        <ResourcesPanelCopyResourcesButton
          getResourceIds={
            resourcesSideParams ? () => getTicketIds(ticketsParams) : undefined
          }
          entityName={entityName}
        />
      }
      side={
        <ActionDropdown
          label="Tickets sorting"
          options={sortingOptions}
          value={sortingValue}
          onSelect={onSortingSelect}
        />
      }
      isPending={isPending}
      isEmpty={!ticketCount}
    >
      <ResourcesPanelList
        scrollRef={scrollRef}
        resources={tickets}
        keyExtractor={keyExtractor}
        entityName={entityName}
        fetchNextPage={fetchNextPage}
        isPending={isPending}
        hasNextPage={hasNextPage}
      />
    </ResourcesPanelSection>
  )
}
